import { inject, Injectable, OnDestroy } from "@angular/core";
import { Auth0Client } from "@auth0/auth0-spa-js";
import { JwtHelperService } from "@auth0/angular-jwt";
import { Subscription } from "rxjs";
import { IAirline } from "../../types/airline";
import { IUserProfile } from "../../types/user";
import { AirlineService } from "./airline.service";
import { StorageService } from "./storageService.service";
import { UserFormatService } from "./userformat.service";
import { AuthApiService } from "../apiservices/authApi.service";

@Injectable()
export class AuthService implements OnDestroy {

  private readonly _auth0 = inject(Auth0Client);
  private readonly _tenants = inject(AirlineService);
  private readonly _storage = inject(StorageService);
  private readonly _userFormat = inject(UserFormatService);
  private readonly _jwtHelper = inject(JwtHelperService);
  private readonly _authApi = inject(AuthApiService);
  private _logoutSocketSubscription: Subscription | null = null;

  /**
   * Redirects user to Auth0 login page.
   */
  public login() {
    this._auth0.loginWithRedirect();
  }

  /**
   * Clears the localStorage and redirects to login
   * @returns {void}
   */
  public logout() {
    this.stopLogoutListener();
    const currentAirline = this._storage.get.currentAirline();
    const currentLanguage = this._storage.get.currentLanguage();
    const lastProfile = this._storage.get.profile();
    this._storage.clear();
    if (lastProfile) this._storage.set.lastUser(lastProfile.userId);
    if (currentAirline) {
      this._storage.set.currentAirline(currentAirline);
      if (currentLanguage) this._storage.set.currentLanguage(currentLanguage);
    }
    setTimeout(() => this._auth0.logout({ returnTo: location.origin }), 1000);
  }

  /**
   * Returns whether the current token is valid or expired.
   * @returns is the token valid?
   */
  public isAuthenticated(): Promise<boolean> {
    const profile = this._storage.get.profile();
    return Promise.all([this._auth0.isAuthenticated(), this._auth0.getUser()])
      .then(([isAuthenticated, user]) => (isAuthenticated && user?.sub == profile?.userId && this._isAuthenticatedSync()));
  }

  private _isAuthenticatedSync() {
    const token = this._storage.get.token();
    return !this._jwtHelper.isTokenExpired(token);
  }

  /**
   * This function creates a WebSocket to listen for a backend-triggered logout event.
   */
  public startLogoutListener() {
    this.stopLogoutListener();
    this._logoutSocketSubscription = this._authApi.observeLogoutMessages<string>()
      .subscribe((event) => this._logoutSocketOnMessage(event));
  }

  private _logoutSocketOnMessage(e: MessageEvent<string>) {
    try {
      const data = JSON.parse(e.data);
      if (!data?.requiresLogout) return;
      this.logout();
    }
    catch { }
  }

  /**
   * Stops subscription to logout messages.
   */
  public stopLogoutListener() {
    this._logoutSocketSubscription?.unsubscribe();
    this._logoutSocketSubscription = null;
  }

  public ngOnDestroy(): void {
    this.stopLogoutListener();
  }

  //#region [main] Handle Authentication callback related functionality.

  /**
   * Handles the redirect form Auth0 and prepares the user data.
   * @returns
   */
  public handleAuthentication(): Promise<IUserProfile> {
    return this._auth0.handleRedirectCallback()
      .then(() => this._auth0.getTokenSilently())
      .then((token) => {
        this._storage.set.token(token);
        return Promise.all([this._auth0.getUser(), this._authApi.getGrantedPermissions()]);
      })
      .then(([user, permissions]) => {
        const profile = this._userFormat.getFormatedAuthUserData(user!, permissions);
        this._storage.set.profile(profile);
        return profile;
      });
  }

  /**
   * Given a userInfo, gets the airline info and returns it as a promise.
   * @param userData
   * @returns User profile
   */
  public saveAirlineOnStorage(userData: IUserProfile): Promise<IUserProfile> {
    const unknownError = new Error("Unknown airline for the current user.");
    if (!userData.tenantCode) return Promise.reject(unknownError);
    return this._tenants.getAirlineById(userData.tenantCode)
      .then((airline) => {
        this._saveAirlineToStorage(airline, unknownError);
        return userData;
      });
  }

  /**
   * Returns a handler that chooses the correct ariline to be saved as the current airline for the logged in user.
   * @param AirlineService
   * @param profile
   * @param error error to be thrown in case airline does not exist.
   * @returns
   */
  private _saveAirlineToStorage(airline: IAirline, error: Error): { userAirline: IAirline, currentAirline: IAirline } {
    if (!airline || !airline.code) throw error;
    this._storage.set.userAirline(airline);
    const currentAirline = this._tenants.currentAirline;
    const finalAirline = currentAirline ? currentAirline : airline;
    if (!currentAirline) this._storage.set.currentAirline(finalAirline);
    this._storage.remove.lastUser();
    return { userAirline: airline, currentAirline: finalAirline };
  }

  //#endregion

  //#region [green] user information getters

  /**
   * If authenticated, returns User Profile Data synchronously.
   * @returns User profile.
   */
  public getUserProfileSync(): IUserProfile | null {
    return this._isAuthenticatedSync() ? this._storage.get.profile() : null;
  }

  /**
   * If authenticated, returns User's Airline Data synchronously.
   * @returns current user's airline.
   */
  public getUserAirlineSync(): IAirline | null {
    return this._isAuthenticatedSync() ? this._storage.get.userAirline() : null;
  }

  /**
   * gets the session timeout configuration for the current user.
   * @returns
   */
  public getUserSessionTimeout(): Promise<number> {
    return this._authApi.getUserSessionTimeout()
      .then(data => data.timeoutAfter)
      .catch(error => {
        console.log(error);
        return -1;
      });
  }

  //#endregion
}
